/**
 * Pure helpers for admin quote-rep reassignment (spec 2026-07-16).
 * No Firebase imports — node-testable. The service layer (quoteService) does
 * the actual Firestore writes using buildReassignPatch.
 */

/** A user row from the `users` collection, as much of it as the picker needs. */
export interface DirectoryUserLike {
  email: string;
  role: string | null;
  fullName?: string;
  whatsapp?: string;
}

/** A resolved reassignment target (the new owning rep). */
export interface ReassignTarget {
  email: string;
  fullName: string;
  whatsapp: string;
}

/** Roles that may OWN a quote (mirror of the create rule's author set). */
const QUOTE_AUTHOR_ROLES = new Set(['SALES', 'ADMIN']);

/**
 * The dotted-field-path patch applied to every version in the chain. Dotted
 * paths update the nested metadata fields in place without overwriting the
 * whole `metadata` map. createdBy is the visibility key; authorName/authorMobile
 * are the denormalized display copies.
 */
export function buildReassignPatch(rep: ReassignTarget): Record<string, string> {
  return {
    'metadata.createdBy': rep.email,
    'metadata.authorName': rep.fullName,
    'metadata.authorMobile': rep.whatsapp,
  };
}

/**
 * Filter the user directory down to valid reassignment targets: quote-authoring
 * roles only, excluding the current owner, sorted by display name. A missing
 * fullName falls back to the email local-part; whatsapp defaults to ''.
 */
export function selectReassignTargets(
  users: DirectoryUserLike[],
  currentOwnerEmail: string,
): ReassignTarget[] {
  const current = currentOwnerEmail.trim().toLowerCase();
  return users
    .filter((u) => u.role != null && QUOTE_AUTHOR_ROLES.has(u.role))
    .filter((u) => u.email.trim().toLowerCase() !== current)
    .map((u) => ({
      email: u.email,
      fullName: u.fullName?.trim() || u.email.split('@')[0],
      whatsapp: u.whatsapp ?? '',
    }))
    .sort((a, b) => a.fullName.localeCompare(b.fullName));
}

/** Distinct truthy projectIds referenced across a quote's version chain. */
export function collectLinkedProjectIds(versions: Array<{ projectId?: string }>): string[] {
  const seen = new Set<string>();
  for (const v of versions) {
    if (v.projectId) seen.add(v.projectId);
  }
  return Array.from(seen);
}
